import { computed, ref } from '@vue/composition-api'
import store from '@/store'
import { apiToastErrorV2 } from '@/@core/utils/toast'
import {
  FETCH_WISHLIST_PRICE_SOURCE_OPTIONS,
  UPDATE_WISHLIST_PRICE_SOURCE,
} from '@/store/modules/shop.module'

export const PRICE_SOURCE_MODES = {
  BASE: 'base',
  PRICE_BOOK: 'priceBook',
  CURRENCY_BOOK: 'currencyBook',
}

const EMPTY_OPTIONS = {
  priceSource: null, baseCurrency: '', priceBooks: [], currencies: [],
}

const SAVE_FAILED = 'Couldn’t save the price source. Please try again.'

const toSelection = source => ({
  mode: source?.type || PRICE_SOURCE_MODES.BASE,
  priceBookId: source?.priceBookId || null,
  currency: source?.currency || null,
})

/** A mode switch drops the other mode's pick, so nothing stale is sent. */
const nextSelection = (current, change) => {
  const merged = { ...current, ...change }
  return {
    mode: merged.mode,
    priceBookId: merged.mode === PRICE_SOURCE_MODES.PRICE_BOOK ? merged.priceBookId : null,
    currency: merged.mode === PRICE_SOURCE_MODES.CURRENCY_BOOK ? merged.currency : null,
  }
}

const isComplete = ({ mode, priceBookId, currency }, options) => {
  if (mode === PRICE_SOURCE_MODES.PRICE_BOOK) return Boolean(priceBookId)
  if (mode === PRICE_SOURCE_MODES.CURRENCY_BOOK) return options.currencies.includes(currency)
  return true
}

const labelFor = ({ mode, priceBookId, currency }, options) => {
  if (mode === PRICE_SOURCE_MODES.CURRENCY_BOOK) return `the ${currency} currency book`
  const book = options.priceBooks.find(item => item._id === priceBookId)
  return book ? book.priceBookName : 'this price book'
}

const toPriceSource = ({ mode, priceBookId, currency }) => {
  if (mode === PRICE_SOURCE_MODES.PRICE_BOOK) return { type: mode, priceBookId }
  if (mode === PRICE_SOURCE_MODES.CURRENCY_BOOK) return { type: mode, currency }
  return null
}

const missingFrom = res => res?.data?.response?.productsWithoutPrice || []

/**
 * Where a wishlist's public link takes its prices from (KP-4586): base prices,
 * one price book, or the currency book in one currency.
 *
 * Only the public link reads this. Saving goes to its own endpoint, separate
 * from the preview settings, and only when the brand changed it.
 */
export function useWishlistPriceSource(wishlistRef) {
  const options = ref({ ...EMPTY_OPTIONS })
  const selection = ref(toSelection(null))
  const saved = ref(toSelection(null))
  const missingProducts = ref([])
  const loadingSource = ref(false)
  const savingSource = ref(false)

  const wishlistId = () => wishlistRef?.value?._id

  const sourceValid = computed(() => isComplete(selection.value, options.value))
  const sourceDirty = computed(() => JSON.stringify(selection.value) !== JSON.stringify(saved.value))
  /** The chosen source's name, for the missing-products message. */
  const sourceLabel = computed(() => labelFor(selection.value, options.value))

  function setSource(change) {
    selection.value = nextSelection(selection.value, change)
    // The list belongs to the source it was checked against.
    missingProducts.value = []
  }

  async function loadSourceOptions() {
    missingProducts.value = []
    if (!wishlistId()) return
    loadingSource.value = true
    try {
      const res = await store.dispatch(FETCH_WISHLIST_PRICE_SOURCE_OPTIONS, { wishlistId: wishlistId() })
      options.value = { ...EMPTY_OPTIONS, ...(res?.data?.response || {}) }
      saved.value = toSelection(options.value.priceSource)
      selection.value = { ...saved.value }
    } catch (err) {
      apiToastErrorV2('Couldn’t load the price options. Please try again.')
    } finally {
      loadingSource.value = false
    }
  }

  const buildPriceSource = () => toPriceSource(selection.value)

  // Base price has no missing products, so there is never anything to recheck.
  const needsSave = recheck => sourceDirty.value
    || (recheck && selection.value.mode !== PRICE_SOURCE_MODES.BASE)

  /**
   * Saves the choice if it changed, or when `recheck` is set (price visibility
   * changed, which changes what counts as "no price"). Resolves the products
   * with no price in the chosen source, or null when the save failed.
   */
  async function saveSource({ recheck = false } = {}) {
    if (!needsSave(recheck)) return []
    savingSource.value = true
    try {
      const res = await store.dispatch(UPDATE_WISHLIST_PRICE_SOURCE, {
        wishlistId: wishlistId(),
        priceSource: buildPriceSource(),
      })
      saved.value = { ...selection.value }
      missingProducts.value = missingFrom(res)
      return missingProducts.value
    } catch (err) {
      // The server says why a source was refused; show that over a generic line.
      apiToastErrorV2(err?.response?.data?.message || SAVE_FAILED)
      return null
    } finally {
      savingSource.value = false
    }
  }

  return {
    priceSourceOptions: options,
    priceSourceSelection: selection,
    missingProducts,
    loadingSource,
    savingSource,
    sourceValid,
    sourceDirty,
    sourceLabel,
    setSource,
    loadSourceOptions,
    buildPriceSource,
    saveSource,
  }
}
